import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react'

/**
 * Attach to a vertically-scrolling container to get an edge-aware fade mask.
 * The container's `data-at-top` / `data-at-bottom` attributes reflect scroll
 * position — pair with the `.edgeFadeScroll` CSS utility (which hides the
 * scrollbar and drives the mask via those attributes) so the top/bottom fade
 * disappears when there is nothing to reveal on that side.
 */
export function useEdgeFadeScroll<T extends HTMLElement = HTMLDivElement>() {
  const ref = useRef<T | null>(null)
  const [edge, setEdge] = useState<{ top: boolean; bottom: boolean }>({ top: true, bottom: true })

  const measure = useCallback(() => {
    const el = ref.current
    if (!el) return
    const atTop = el.scrollTop <= 1
    const atBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - 1
    setEdge((prev) => (prev.top === atTop && prev.bottom === atBottom ? prev : { top: atTop, bottom: atBottom }))
  }, [])

  useLayoutEffect(() => { measure() }, [measure])

  useEffect(() => {
    const el = ref.current
    if (!el) return
    const ro = new ResizeObserver(() => measure())
    ro.observe(el)
    for (const child of Array.from(el.children)) ro.observe(child as Element)
    const mo = new MutationObserver(() => {
      ro.disconnect()
      ro.observe(el)
      for (const child of Array.from(el.children)) ro.observe(child as Element)
      measure()
    })
    mo.observe(el, { childList: true })
    return () => { ro.disconnect(); mo.disconnect() }
  }, [measure])

  const scrollProps = {
    onScroll: measure,
    'data-at-top': edge.top ? 'true' : 'false',
    'data-at-bottom': edge.bottom ? 'true' : 'false',
  } as const

  return { ref, scrollProps, remeasure: measure, atTop: edge.top, atBottom: edge.bottom }
}
